"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

interface Result {
  id: string;
  name: string;
  path: string;
}

export default function TaxonomyPicker({
  kind,
  label,
  initialPath,
  onSelect,
}: {
  kind: "category" | "region";
  label: string;
  initialPath?: string;
  onSelect: (id: string, path: string) => void;
}) {
  const t = useTranslations("taxonomyPicker");
  const tc = useTranslations("common");
  const locale = useLocale();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [selectedPath, setSelectedPath] = useState(initialPath ?? "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (query.trim().length === 0) {
        if (!cancelled) setResults([]);
        return;
      }
      const res = await fetch(
        `/api/${kind === "category" ? "categories" : "regions"}/autocomplete?q=${encodeURIComponent(query)}&locale=${locale}`
      );
      const json = await res.json();
      if (!cancelled) setResults(json.results ?? []);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, kind, locale]);

  return (
    <div className="relative flex flex-col gap-1">
      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</label>
      {selectedPath && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center justify-between rounded-lg border border-zinc-300 px-3 py-2 text-left text-sm dark:border-zinc-700"
        >
          <span>{selectedPath}</span>
          <span className="text-xs text-zinc-500">{tc("change")}</span>
        </button>
      ) : (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={kind === "category" ? t("categoryPlaceholder") : t("regionPlaceholder")}
          className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        />
      )}
      {open && results.length > 0 && (
        <ul className="absolute top-full z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  setSelectedPath(r.path);
                  setQuery("");
                  setResults([]);
                  setOpen(false);
                  onSelect(r.id, r.path);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                {r.path}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
